/**
 * Formats file size in bytes to a human-readable string
 * 
 * @param bytes - The file size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Gets the appropriate icon class for a file based on its extension
 * 
 * @param fileName - The name of the file
 * @returns Bootstrap glyphicon class name
 */
export const getFileIcon = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
        case 'pdf':
        case 'docx':
        case 'doc':
        case 'xlsx':
        case 'xls':
        case 'csv':
        case 'txt':
            return 'glyphicon-file';
        default:
            return 'glyphicon-file';
    }
};

/**
 * Calculates summary information for a list of uploaded files
 * 
 * @param files - Array of files with File objects
 * @returns Object containing total size and unique file types
 */
export const calculateFileSummary = (files: Array<{ file: File; fileName: string; fileSize: number }>) => {
    const totalSize = files.reduce((sum, item) => sum + item.fileSize, 0);
    const fileTypes = Array.from(
        new Set(
            files
                .map(f => f.fileName.split('.').pop()?.toUpperCase())
                .filter(Boolean)
        )
    )
        .slice(0, 3)
        .join(', ');

    return { totalSize, fileTypes };
};

/**
 * Supported file types for upload
 */
export const SUPPORTED_FILE_TYPES = [
    '.pdf', '.docx', '.xlsx', '.csv', '.txt', 
    '.ppt', '.pptx', '.png', '.doc', '.xml', 
    '.jpg', '.jpeg', '.ipc1752a'
];

/**
 * Maximum file size in bytes (20MB)
 */
export const MAX_FILE_SIZE = 20 * 1024 * 1024;

/**
 * Checks file signature (magic numbers) to validate actual file content
 * 
 * @param file - The file to check
 * @param extension - The file extension
 * @returns Promise resolving to true if signature is valid
 */
export const checkFileSignature = async (file: File, extension: string): Promise<boolean> => {
    return new Promise((resolve) => {
        const reader = new FileReader();

        reader.onloadend = (e) => {
            if (!e.target?.result) {
                resolve(false);
                return;
            }

            const arr = new Uint8Array(e.target.result as ArrayBuffer);

            // Check file signatures (magic numbers)
            switch (extension) {
                case '.pdf':
                    // PDF starts with %PDF (25 50 44 46)
                    if (arr.length >= 4) {
                        const isPdf = arr[0] === 0x25 && arr[1] === 0x50 && arr[2] === 0x44 && arr[3] === 0x46;
                        resolve(isPdf);
                    } else {
                        resolve(false);
                    }
                    break;

                case '.docx':
                case '.xlsx':
                case '.pptx':
                    // DOCX, XLSX, and PPTX are ZIP files (PK signature: 50 4B)
                    if (arr.length >= 4) {
                        const isZip = arr[0] === 0x50 && arr[1] === 0x4B &&
                            (arr[2] === 0x03 || arr[2] === 0x05 || arr[2] === 0x07) &&
                            (arr[3] === 0x04 || arr[3] === 0x06 || arr[3] === 0x08);
                        resolve(isZip);
                    } else {
                        resolve(false);
                    }
                    break;

                case '.ppt':
                case '.doc':
                    // PPT and DOC files (older format) are OLE2 structured storage files
                    if (arr.length >= 8) {
                        // Check for OLE2 signature: D0 CF 11 E0 A1 B1 1A E1
                        const isOle2 = arr[0] === 0xD0 && arr[1] === 0xCF &&
                            arr[2] === 0x11 && arr[3] === 0xE0 &&
                            arr[4] === 0xA1 && arr[5] === 0xB1 &&
                            arr[6] === 0x1A && arr[7] === 0xE1;
                        resolve(isOle2);
                    } else {
                        resolve(false);
                    }
                    break;

                case '.png':
                    // PNG files start with 89 50 4E 47 0D 0A 1A 0A
                    if (arr.length >= 8) {
                        const isPng = arr[0] === 0x89 && arr[1] === 0x50 &&
                            arr[2] === 0x4E && arr[3] === 0x47 &&
                            arr[4] === 0x0D && arr[5] === 0x0A &&
                            arr[6] === 0x1A && arr[7] === 0x0A;
                        resolve(isPng);
                    } else {
                        resolve(false);
                    }
                    break;

                case '.jpg':
                case '.jpeg':
                    // JPEG files start with FF D8 FF
                    if (arr.length >= 3) {
                        const isJpeg = arr[0] === 0xFF && arr[1] === 0xD8 && arr[2] === 0xFF;
                        resolve(isJpeg);
                    } else {
                        resolve(false);
                    }
                    break;

                case '.csv':
                case '.txt':
                case '.xml':
                case '.IPC1752A':
                    // For text files, check if content is valid text (not binary)
                    if (arr.length >= 2) {
                        // Reject if it starts with MZ (EXE/DLL signature)
                        if (arr[0] === 0x4D && arr[1] === 0x5A) {
                            resolve(false);
                            return;
                        }
                        // Reject if it starts with PK (ZIP signature)
                        if (arr[0] === 0x50 && arr[1] === 0x4B) {
                            resolve(false);
                            return;
                        }
                        // Reject if it starts with GIF signature
                        if (arr.length >= 3 && arr[0] === 0x47 && arr[1] === 0x49 && arr[2] === 0x46) {
                            resolve(false);
                            return;
                        }

                        // Accept if it doesn't match known binary signatures
                        resolve(true);
                    } else {
                        resolve(true); // Empty or very small files are acceptable for text
                    }
                    break;

                default:
                    resolve(false);
            }
        };

        reader.onerror = () => resolve(false);

        // Read first 512 bytes to check signature
        const blob = file.slice(0, 512);
        reader.readAsArrayBuffer(blob);
    });
};

/**
 * Validates a file based on extension, size, and signature
 * 
 * @param file - The file to validate
 * @returns Promise resolving to true if file is valid
 */
export const validateFile = async (file: File): Promise<boolean> => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    // Check if extension is supported
    if (!SUPPORTED_FILE_TYPES.includes(extension)) {
        return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
        return false;
    }

    // Validate actual file content by checking file signature
    const isValidSignature = await checkFileSignature(file, extension);

    return isValidSignature;
};
